import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { WhatsappLogo, MagnifyingGlass, MapPin, Phone, SignIn, Car as CarIcon } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

export default function PublicCatalog() {
  const [cars, setCars] = useState([]);
  const [settings, setSettings] = useState({});
  const [q, setQ] = useState("");
  const [fBrand, setFBrand] = useState("all");
  const [fTrans, setFTrans] = useState("all");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    Promise.all([
      publicApi.get("/public/cars"),
      publicApi.get("/public/settings"),
    ]).then(([c, s]) => { setCars(c.data); setSettings(s.data); })
      .finally(() => setBusy(false));
  }, []);

  const brands = useMemo(() => Array.from(new Set(cars.map((c) => c.brand))).sort(), [cars]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return cars.filter((c) => {
      if (fBrand !== "all" && c.brand !== fBrand) return false;
      if (fTrans !== "all" && (c.transmission || "").toUpperCase() !== fTrans) return false;
      if (!s) return true;
      return (
        (c.brand || "").toLowerCase().includes(s) ||
        (c.model || "").toLowerCase().includes(s) ||
        (c.type || "").toLowerCase().includes(s) ||
        String(c.year || "").includes(s)
      );
    });
  }, [cars, q, fBrand, fTrans]);

  const waHref = (car) => {
    const num = (settings.whatsapp_number || "").replace(/[^0-9]/g, "");
    if (!num) return "#";
    const msg = encodeURIComponent(
      `Halo ${settings.name || "Showroom"}, saya tertarik dengan ${car.brand} ${car.model}${car.type ? " " + car.type : ""} tahun ${car.year} (${car.transmission || "-"}). Bisa info lebih lanjut?`
    );
    return `https://wa.me/${num}?text=${msg}`;
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      {/* Header */}
      <header className="bg-[#0A0A0A] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="logo" className="w-10 h-10 object-contain bg-white p-1" />
            ) : (
              <CarIcon size={28} weight="bold" />
            )}
            <div>
              <div className="font-display text-lg font-bold leading-none">{settings.name || "SHOWROOM"}</div>
              {settings.tagline && <div className="text-[11px] tracking-widest uppercase text-white/50 mt-1">{settings.tagline}</div>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {settings.whatsapp_number && (
              <a
                href={`https://wa.me/${(settings.whatsapp_number || "").replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                data-testid="header-wa"
                className="hidden md:inline-flex items-center gap-2 px-4 h-10 bg-[#25D366] hover:bg-[#1FB855] text-white uppercase tracking-wider text-xs font-semibold"
              >
                <WhatsappLogo size={16} weight="fill" /> {settings.whatsapp_number}
              </a>
            )}
            <Link
              to="/login"
              data-testid="login-link"
              className="inline-flex items-center gap-2 px-4 h-10 border border-white/40 hover:bg-white hover:text-[#0A0A0A] uppercase tracking-wider text-xs font-semibold transition-colors"
            >
              <SignIn size={14} weight="bold" /> Login Admin
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="text-[11px] tracking-[0.3em] uppercase text-gray-500 mb-3">Stok Terkini</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0A0A]">
          Pilih Mobil Impianmu.
        </h1>
        <p className="text-gray-600 mt-3 max-w-xl">
          {filtered.length} unit tersedia — hubungi kami langsung via WhatsApp untuk info harga, test drive, dan simulasi kredit.
        </p>

        {/* Filters */}
        <div className="mt-6 flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari merek, model, atau tahun..."
              data-testid="catalog-search"
              className="w-full h-11 pl-10 pr-4 border border-gray-300 focus:outline-none focus:border-[#0A0A0A] bg-white"
            />
          </div>
          <select
            value={fBrand}
            onChange={(e) => setFBrand(e.target.value)}
            data-testid="catalog-brand"
            className="h-11 px-4 border border-gray-300 bg-white focus:outline-none focus:border-[#0A0A0A]"
          >
            <option value="all">Semua Merek</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select
            value={fTrans}
            onChange={(e) => setFTrans(e.target.value)}
            data-testid="catalog-trans"
            className="h-11 px-4 border border-gray-300 bg-white focus:outline-none focus:border-[#0A0A0A]"
          >
            <option value="all">Semua Transmisi</option>
            <option value="MT">Manual (MT)</option>
            <option value="AT">Matic (AT)</option>
          </select>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        {busy ? (
          <div className="text-center py-16 text-gray-500">Memuat katalog...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500">Tidak ada mobil yang cocok dengan pencarian.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((c) => (
              <div key={c.id} data-testid={`catalog-car-${c.id}`} className="bg-white border border-gray-200 overflow-hidden group hover:shadow-lg transition-shadow">
                <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center overflow-hidden">
                  {c.image_url ? (
                    <img src={c.image_url} alt={`${c.brand} ${c.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <CarIcon size={64} className="text-gray-300" />
                  )}
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 font-mono-num">{c.account_number || "—"}</div>
                  <div className="font-display text-xl font-bold mt-1">{c.brand} {c.model}</div>
                  {c.type && <div className="text-sm text-gray-600">{c.type}</div>}
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-wider">
                    <span className="px-2 py-1 bg-gray-100 font-mono-num">{c.year}</span>
                    {c.transmission && (
                      <span className={`px-2 py-1 font-semibold ${c.transmission === "AT" ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"}`}>
                        {c.transmission === "AT" ? "Matic" : c.transmission === "MT" ? "Manual" : c.transmission}
                      </span>
                    )}
                    {c.color && <span className="px-2 py-1 bg-gray-100">{c.color}</span>}
                  </div>
                  {c.description && (
                    <p className="text-xs text-gray-600 mt-3 line-clamp-2">{c.description}</p>
                  )}
                  <a
                    href={waHref(c)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`wa-${c.id}`}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 bg-[#25D366] hover:bg-[#1FB855] text-white uppercase tracking-wider text-xs font-semibold transition-colors"
                  >
                    <WhatsappLogo size={16} weight="fill" /> Tanya via WhatsApp
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white/80">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="font-display text-xl font-bold text-white">{settings.name || "SHOWROOM"}</div>
            {settings.tagline && <div className="text-xs mt-2 text-white/50">{settings.tagline}</div>}
          </div>
          <div className="space-y-2 text-sm">
            {settings.address_office && (
              <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 shrink-0" /> {settings.address_office}{settings.city ? `, ${settings.city}` : ""}</div>
            )}
            {settings.phone_office && (
              <div className="flex items-start gap-2"><Phone size={14} className="mt-0.5 shrink-0" /> <span className="font-mono-num">{settings.phone_office}</span></div>
            )}
          </div>
          <div>
            {settings.whatsapp_number && (
              <a
                href={`https://wa.me/${(settings.whatsapp_number || "").replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 h-11 bg-[#25D366] hover:bg-[#1FB855] text-white uppercase tracking-wider text-xs font-semibold"
              >
                <WhatsappLogo size={16} weight="fill" /> Chat via WhatsApp
              </a>
            )}
          </div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-[11px] text-white/40">
          © {new Date().getFullYear()} {settings.name || "Showroom"} · All rights reserved
        </div>
      </footer>
    </div>
  );
}
